import type { ComposerTranslation } from 'vue-i18n'
import {
  ApplyScope,
  BlockDirection,
  BlockProtocol,
  DisposeActionType,
  SecurityDisposeStatus,
  ThreatEntityType,
} from './constants/enums'
import type { DisposeFormState, SecurityEventDetail } from './types'

export type DisposeActionOption = {
  value: DisposeActionType
  label: string
  desc: string
  disabled: boolean
  tip?: string
  showApplyScope: boolean
}

function trustActionFor (type?: ThreatEntityType): DisposeActionType | null {
  const map: Partial<Record<ThreatEntityType, DisposeActionType>> = {
    [ThreatEntityType.Ip]: DisposeActionType.TrustIp,
    [ThreatEntityType.Url]: DisposeActionType.TrustUrl,
    [ThreatEntityType.Process]: DisposeActionType.TrustProcess,
    [ThreatEntityType.BehaviorHash]: DisposeActionType.TrustHash,
    [ThreatEntityType.File]: DisposeActionType.TrustFile,
  }
  return type != null ? map[type] ?? null : null
}

export {
  buildStrategyName,
  extractDomainFromThreat,
  isFileContextAction,
  isStatusOnlyAction,
  isTrustAction,
} from './dispose-strategy-utils'

export function buildDefaultForm (detail: SecurityEventDetail): DisposeFormState {
  const directionFallback =
    detail.threat_entity_type === ThreatEntityType.Ip
      ? BlockDirection.Outbound
      : BlockDirection.Bidirectional
  return {
    protocol: String(detail.default_block_protocol ?? BlockProtocol.Any),
    direction: String(detail.default_block_direction ?? directionFallback),
    apply_scope: ApplyScope.Local,
  }
}

export function buildDisposeActionOptions (
  detail: SecurityEventDetail,
  t: ComposerTranslation,
  permissions: { canBlockIp: boolean; canBlockUrl: boolean },
): DisposeActionOption[] {
  const type = detail.threat_entity_type
  const isUser = type === ThreatEntityType.User

  const actionLabel = (value: DisposeActionType) => t(`securityEvent.disposeAction.${value}`)

  const actionDesc = (value: DisposeActionType) => t(`securityEvent.disposeActionDesc.${value}`)

  const primary: DisposeActionOption[] = []
  const status: DisposeActionOption[] = []
  const isHandled = detail.handle_status === SecurityDisposeStatus.Handled
  const isIgnored = detail.handle_status === SecurityDisposeStatus.Ignored

  // 已处置不显示「标记为已处置」
  if (!isHandled) {
    status.push({
      value: DisposeActionType.MarkHandled,
      label: actionLabel(DisposeActionType.MarkHandled),
      desc: actionDesc(DisposeActionType.MarkHandled),
      disabled: false,
      showApplyScope: false,
    })
  }

  // 已处置、已忽略不显示「忽略本次告警」
  if (!isHandled && !isIgnored) {
    status.push({
      value: DisposeActionType.MarkIgnored,
      label: actionLabel(DisposeActionType.MarkIgnored),
      desc: actionDesc(DisposeActionType.MarkIgnored),
      disabled: false,
      showApplyScope: false,
    })
  }
  const trust: DisposeActionOption[] = []

  if (!isUser && type === ThreatEntityType.Ip) {
    primary.push({
      value: DisposeActionType.BlockIp,
      label: actionLabel(DisposeActionType.BlockIp),
      desc: actionDesc(DisposeActionType.BlockIp),
      disabled: !permissions.canBlockIp || !!detail.ip_blocked,
      tip: detail.ip_blocked
        ? t('securityEvent.blockIpDone')
        : !permissions.canBlockIp
          ? t('securityEvent.noPermissionBlockIp')
          : undefined,
      showApplyScope: true,
    })
  }

  if (!isUser && type === ThreatEntityType.Url) {
    primary.push({
      value: DisposeActionType.BlockUrl,
      label: actionLabel(DisposeActionType.BlockUrl),
      desc: actionDesc(DisposeActionType.BlockUrl),
      disabled: !permissions.canBlockUrl || !!detail.domain_blocked,
      tip: detail.domain_blocked
        ? t('securityEvent.blockUrlDone')
        : !permissions.canBlockUrl
          ? t('securityEvent.noPermissionBlockUrl')
          : undefined,
      showApplyScope: true,
    })
  }

  // if (!isUser && type === ThreatEntityType.File) {
  //   primary.push({
  //     value: DisposeActionType.IsolateFile,
  //     label: actionLabel(DisposeActionType.IsolateFile),
  //     desc: actionDesc(DisposeActionType.IsolateFile),
  //     disabled: true,
  //     tip: t('securityEvent.isolateFilePhase1'),
  //     showApplyScope: false,
  //   })
  // }

  if (!isUser && type === ThreatEntityType.Process) {
    primary.push({
      value: DisposeActionType.KillProcess,
      label: actionLabel(DisposeActionType.KillProcess),
      desc: actionDesc(DisposeActionType.KillProcess),
      disabled: !!detail.process_killed,
      tip: detail.process_killed ? t('securityEvent.processKillDone') : undefined,
      showApplyScope: false,
    })
  }

  if (!isUser) {
    const trustAction = trustActionFor(type)
    if (trustAction && type !== ThreatEntityType.User) {
      trust.push({
        value: trustAction,
        label: actionLabel(trustAction),
        desc: actionDesc(trustAction),
        disabled: false,
        showApplyScope: false,
      })
    }
  }

  return [...primary, ...status, ...trust]
}
